import { DocumentNode, useQuery } from "@apollo/client";
import { useEffect, useMemo, useState } from "react";

import { GET_ORDER_LISTING_HISTORY, HistoryOrderListResponse } from "../graphql/queries/getOrderListingHistory";
import { buildCentralParcelQueryVariables } from "../utils/buildCentralParcelQueryVariables";
import { stripExceptionFilters } from "../utils/stripExceptionFilters";
import { OrderType } from "../../../../graphql/types/OrderType";
import { OrderFiltersType } from "../../../../graphql/types/OrderFiltersType";
import { ParcelQueryResult } from "./useCentralParcelQuery";

/**
 * 历史包裹查询（已归档订单）。
 *
 * 与 useCentralParcelQuery 的差别只有两点：打的是 getOrderListingHistory，
 * 且请求前剥掉异常信息相关筛选条件。变量构建逻辑与列表页共用同一个函数。
 *
 * 后端对异常筛选字段本身也会忽略，这里剥掉是为了别把无效条件发出去
 * ——请求体更干净，排查问题时不会误以为条件生效了。
 */
export const useCentralParcelHistoryQuery = (
    filters: OrderFiltersType,
    currentPage: number,
    pageSize: number,
    sortField?: string | null,
    sortDirection?: 'ASC' | 'DESC' | null,
    options: {
        /** 按系统切换后端入口（WMS / 客服），默认总部 getOrderListingHistory；返回字段均别名为 getOrderListingHistory */
        query?: DocumentNode;
        /** 系统级固定筛选条件（如 WMS 当前仓库 currentWarehouseId / sortingCode），覆盖页面筛选 */
        extraFilters?: Record<string, any>;
        /** 为 true 时不发请求（如 WMS 当前仓库尚未就绪） */
        skip?: boolean;
    } = {}
): ParcelQueryResult => {
    const { query = GET_ORDER_LISTING_HISTORY, extraFilters, skip = false } = options;
    const [parcelData, setParcelData] = useState<OrderType[]>([]);
    const [loading, setLoading] = useState(true);
    const [total, setTotal] = useState(0);
    const [accessiblePages, setAccessiblePages] = useState(0);

    const buildQueryVariables = () => {
        const variables = buildCentralParcelQueryVariables(
            stripExceptionFilters(filters),
            currentPage,
            pageSize,
            sortField,
            sortDirection
        );
        return extraFilters ? { ...variables, filters: { ...variables.filters, ...extraFilters } } : variables;
    };
    const extraFiltersKey = JSON.stringify(extraFilters ?? null);

    const memoizedVariables = useMemo(
        () => buildQueryVariables(),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [filters, currentPage, pageSize, sortField, sortDirection, extraFiltersKey]
    );

    const { data, loading: queryLoading, refetch } = useQuery<HistoryOrderListResponse>(
        query,
        { variables: memoizedVariables, skip }
    );

    useEffect(() => {
        setLoading(queryLoading);
        if (data?.getOrderListingHistory) {
            setParcelData(data.getOrderListingHistory.content ?? []);
            setTotal(data.getOrderListingHistory.totalElements ?? 0);
            setAccessiblePages(data.getOrderListingHistory.accessiblePages ?? 0);
        }
    }, [data, queryLoading]);

    return {
        parcelData,
        loading,
        refetch,
        totalElements: total,
        accessiblePages,
        buildQueryVariables
    };
};
